import {
    Dictionary,
    DropDown,
    useWorkplaceState,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getChannels } from 'axios/merchantApi';
import { getOutletsAll } from 'axios/outletsApi';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { PreorderFilterType } from 'models/api/preorder';
import { SelectOption } from 'models/api/prop';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery, UseQueryResult } from 'react-query';
import { Col, Row } from 'reactstrap';
import { MerchantFilterType } from 'models/api/merchant';

type PreorderFilterProps = {
    orderStatuses: DropDown[];
    tradeOrgs: DropDown[];
    sources: DropDown[];
    brandCodes: DropDown[];
    productTypes: DropDown[];
    getOrderStatusesQuery: UseQueryResult<DropDown[], unknown>;
    getTraderOrgsQuery: UseQueryResult<SelectOption[], unknown>;
    getDictsQuery: UseQueryResult<Dictionary<DropDown[]>, unknown>;
    isFetchingBrandCodes: boolean;
};

const PreorderFilter = ({
    orderStatuses,
    tradeOrgs,
    sources,
    brandCodes,
    productTypes,
    getOrderStatusesQuery,
    getTraderOrgsQuery,
    getDictsQuery,
    isFetchingBrandCodes,
}: PreorderFilterProps) => {
    const { t } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const { handleError } = useWorkplaceState();

    const { watch, setValue } = useFormContext<PreorderFilterType>();
    const tradeOrgId = watch('tradeOrgId');

    const { isFetching: outletsFetching, data: outlets } = useQuery(
        ['Outlets', tradeOrgId],
        () => getOutletsAll(api, tradeOrgId ?? null),
        {
            enabled: !!tradeOrgId,
            onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
        }
    );

    const { isFetching: isChannelsFetching, data: channels } = useQuery(
        'preorderChannels',
        () => getChannels(api, false),
        {
            onError: (err) => handleError(err),
            enabled: hasRole('merchantGetChannels'),
        }
    );

    useEffect(() => {
        setValue('outletId', null);
    }, [tradeOrgId]);

    return (
        <>
            <ValidatedDropDownList<PreorderFilterType>
                name="statusCode"
                items={orderStatuses ?? []}
                isLoading={getOrderStatusesQuery.isFetching}
                disabled={!getOrderStatusesQuery.data?.length}
                label={t('BackOfficeOrderStatus')}
            />
            <ValidatedInput<PreorderFilterType>
                id="ds-order-info"
                name="orderInfo"
                label={t('BackOfficeClientInfo')}
            />
            <Row>
                <Col>
                    <BOValidatedDatePicker<PreorderFilterType>
                        id="ds-start-date"
                        name="sd"
                        inputProps={{ label: t('BackOfficeStartDate') }}
                    />
                </Col>
                <Col>
                    <BOValidatedDatePicker<PreorderFilterType>
                        id="ds-end-date"
                        name="ed"
                        inputProps={{ label: t('BackOfficeEndDate') }}
                    />
                </Col>
            </Row>
            <Row>
                <ValidatedDropDownList<PreorderFilterType>
                    name="tradeOrgId"
                    label={t('BackOfficeTradeOrg')}
                    isLoading={getTraderOrgsQuery.isLoading}
                    items={tradeOrgs}
                    onValueChange={() => setValue('outletId', null)}
                />
                <ValidatedDropDownList<PreorderFilterType>
                    name="outletId"
                    label={t('BackOfficeOutlets')}
                    disabled={tradeOrgId == null || !outlets}
                    isLoading={outletsFetching}
                    items={outlets || []}
                />
            </Row>
            <Row>
                <Col xs="6">
                    <ValidatedDropDownList<PreorderFilterType>
                        name="source"
                        items={sources}
                        isLoading={getDictsQuery.isFetching}
                        disabled={!sources.length}
                        label={t('BackOfficeSource')}
                    />
                </Col>
                {hasRole('merchantGetChannels') && (
                    <Col xs="6">
                        <ValidatedDropDownList<PreorderFilterType>
                            name="channel"
                            items={channels ?? []}
                            isLoading={isChannelsFetching}
                            disabled={!channels?.length}
                            label={t('BackOfficeChannel')}
                        />
                    </Col>
                )}
            </Row>
            {hasRole('visibleBrandCodesFilter') && (
                <Row>
                    <Col>
                        <ValidatedDropDownList<MerchantFilterType>
                            name="brandCode"
                            items={brandCodes}
                            isLoading={!isFetchingBrandCodes}
                            disabled={!brandCodes.length}
                            label={t('BackOfficeBrandCode')}
                        />
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs="6">
                    <ValidatedDropDownList<PreorderFilterType>
                        name="productTypeId"
                        items={productTypes}
                        disabled={!productTypes.length}
                        label={t('BackOfficeProductType')}
                    />
                </Col>
            </Row>
        </>
    );
};

export default PreorderFilter;
